import React, { forwardRef } from "react";
import type { FC, JSX, PropsWithChildren } from "react";

import cn from "classnames";

import { UNI_CARD_DISPLAY_NAME } from "./utils/core";
import { createDisplayNameForUniCardElement } from "./utils/getDisplayName";
import { createTestIdForUniCardElement } from "./utils/getTestId";
import { ButtonCore } from "../../helpers/common/ButtonCore";
import { ButtonDiv } from "../../helpers/common/ButtonDiv";
import { useMouseDownAnimation } from "../../hooks/useMouseDownAnimation";
import { getChildrenRecord } from "../../utils/react";

import type { UniCardProps } from "./types";

import classes from "./UniCard.styl";

const UNI_CARD_TEST_ID = "UniCard-test-id";
const UNI_CARD_CONTENT_TEST_ID = createTestIdForUniCardElement("content");

interface IUniCardContent extends PropsWithChildren {}

const UniCardContent: FC<IUniCardContent> = ({ children }) => {
    const { Media, Text, Bottom, ButtonsView } = getChildrenRecord(
        children,
        "UniCard",
        {
            Media: {
                required: false,
            },
            Text: {
                required: true,
            },
            Unknown: {
                skipElement: true,
            },
        },
    );

    return (
        // eslint-disable-next-line ok-custom-rules/check-attribute
        <div
            className={classes["uni-card-content"]}
            data-tsid={UNI_CARD_CONTENT_TEST_ID}
        >
            <div className={classes["uni-card-media"]}>{Media}</div>
            {Text}
            {Bottom}
            {ButtonsView}
        </div>
    );
};

UniCardContent.displayName = createDisplayNameForUniCardElement("Content");

/**
 * Вертикальный контейнер, внутри которого всегда находится изображение и блок с контентом в виде текста. В нижней части может показывать дополнительные компоненты и кнопки.
 *
 * На стороне дизайна служит конструктором, в который можно положить как готовые предустановленные компоненты, так и локальные, не имеющие готовой интерпретации в ДС.
 *
 * Систематизирует динамические отступы, обрабатывает состояния.
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?node-id=44-75773&p=f&m=dev" target="_blank">Спека</a>
 */
export const UniCard = forwardRef<HTMLDivElement, UniCardProps>(
    (
        {
            children,
            customVarsStyles,
            state = "enabled",
            size = "220",
            "data-tsid": tsid,
            ...props
        },
        ref,
    ): JSX.Element => {
        const { clicked, mousedownHandler } = useMouseDownAnimation();

        const wrapperClasses = cn(
            classes["wrapper"],
            classes[`__size-${size}`],
            customVarsStyles,
        );

        const containerClasses = cn(
            classes["uni-card"],
            classes[`__state-${state}`],
            {
                [classes["__clicked"]]: clicked,
            },
        );

        const dataTestId = tsid || UNI_CARD_TEST_ID;
        const buttonState = state === "enabled" ? undefined : state;

        return (
            <div className={wrapperClasses}>
                <ButtonCore
                    as={ButtonDiv}
                    {...props}
                    wrapperClassName={containerClasses}
                    onMouseDown={mousedownHandler}
                    state={buttonState}
                    data-tsid={dataTestId}
                    data-uikit={UNI_CARD_DISPLAY_NAME}
                    ref={ref}
                >
                    <UniCardContent>{children}</UniCardContent>
                </ButtonCore>
            </div>
        );
    },
);

UniCard.displayName = UNI_CARD_DISPLAY_NAME;

export default UniCard;
